import { ROUTES } from '@/routes/paths';

const isRecord = (value: unknown): value is Record<string, unknown> => {
  return typeof value === 'object' && value !== null;
};

const APP_SELLER_INFO_KEYS = ['AppSellerInfo', 'appSellerInfo'] as const;
const SELLKEY_INFO_KEYS = ['SellkeyInfo', 'sellkeyInfo'] as const;
const SUBSCRIPTION_KEYS = [
  'IsAnySubscribed',
  'isAnySubscribed',
  'IsSubcribed',
  'isSubcribed',
] as const;
const PRE_PAYMENT_KEYS = ['IsPrePayment', 'isPrePayment'] as const;
const MEMBERSHIP_STATE_KEYS = ['MembershipState', 'membershipState'] as const;
const PAID_MEMBERSHIP_STATES = new Set([
  'WEB_SUBSCRIBED',
  'APP_SUBSCRIBED',
  'APP_SUBSCRIBED_WEB_CONVERSION_PENDING',
]);

const PAID_MEMBERSHIP_PATH_PREFIXES = [
  ROUTES.dashboard,
  ROUTES.statistics,
  ROUTES.orders,
  ROUTES.monitoring,
  ROUTES.productOptimization,
  ROUTES.market,
  ROUTES.PcMarketConnectinoComplete,
  ROUTES.updateInfo,
  ROUTES.productShipment,
  ROUTES.productShipmentGuide,
  ROUTES.inquiryList,
  ROUTES.inquiryDetail,
  ROUTES.inquiryGuide,
  ROUTES.templatesInquiryList,
  ROUTES.templatesInquiryDetail,
  ROUTES.discoverMain,
  ROUTES.aiChatBot,
  ROUTES.statisticsMain,
  ROUTES.makeName,
  ROUTES.changeName,
  ROUTES.diagnoseProduct,
].filter(Boolean);

const KST_OFFSET_MS = 9 * 60 * 60 * 1000;
const EARLY_BIRD_PROMOTION_START_TIME = Date.UTC(2026, 4, 1) - KST_OFFSET_MS;
const EARLY_BIRD_PROMOTION_END_EXCLUSIVE_TIME = Date.UTC(2026, 6, 1) - KST_OFFSET_MS;

const MEMBERSHIP_EARLY_BIRD_PROMOTION_MESSAGE =
  '멤버십 가입하고 3개월간 월 100원으로 셀러박스를 이용해보세요🎉';
const MEMBERSHIP_FIRST_PAYMENT_PROMOTION_MESSAGE =
  '멤버십 가입하고 월 100원으로 셀러박스를 이용해보세요🎉';
const MEMBERSHIP_STANDARD_PROMOTION_MESSAGE =
  '멤버십 가입하고 월 15,000원으로 셀러박스를 이용해보세요🎉';

export const hasPaidMembershipAccess = (serviceInfo: unknown) => {
  if (!isRecord(serviceInfo)) return false;

  const appSellerInfo = APP_SELLER_INFO_KEYS.map((key) => serviceInfo[key]).find(isRecord);
  if (!appSellerInfo) {
    return hasPaidMembershipState(serviceInfo) || hasLegacySellkeySubscriptionAccess(serviceInfo);
  }

  if (hasPaidMembershipState(serviceInfo) || hasPaidMembershipState(appSellerInfo)) return true;

  return hasSubscriptionFlag(appSellerInfo) || hasLegacySellkeySubscriptionAccess(serviceInfo);
};

export const isMembershipEarlyBirdPromotionPeriodInKst = (date = new Date()) => {
  const time = date.getTime();

  return (
    time >= EARLY_BIRD_PROMOTION_START_TIME && time < EARLY_BIRD_PROMOTION_END_EXCLUSIVE_TIME
  );
};

export const getMembershipConversionOverlayMessage = (
  serviceInfo: unknown,
  date = new Date(),
) => {
  if (isMembershipEarlyBirdPromotionPeriodInKst(date)) {
    return MEMBERSHIP_EARLY_BIRD_PROMOTION_MESSAGE;
  }

  return isFirstMembershipPaymentEligible(serviceInfo)
    ? MEMBERSHIP_FIRST_PAYMENT_PROMOTION_MESSAGE
    : MEMBERSHIP_STANDARD_PROMOTION_MESSAGE;
};

const isFirstMembershipPaymentEligible = (serviceInfo: unknown) => {
  if (!isRecord(serviceInfo)) return true;

  const appSellerInfo = APP_SELLER_INFO_KEYS.map((key) => serviceInfo[key]).find(isRecord);
  const paymentSource = appSellerInfo ?? serviceInfo;
  const explicitPrePayment = PRE_PAYMENT_KEYS.map((key) => paymentSource[key]).find(
    (value): value is boolean => typeof value === 'boolean',
  );

  if (explicitPrePayment !== undefined) return explicitPrePayment;

  return !SUBSCRIPTION_KEYS.some((key) => paymentSource[key] === true);
};

const hasPaidMembershipState = (record: Record<string, unknown>) => {
  return MEMBERSHIP_STATE_KEYS.some((key) => {
    const membershipState = record[key];

    return typeof membershipState === 'string' && PAID_MEMBERSHIP_STATES.has(membershipState);
  });
};

const hasSubscriptionFlag = (record: Record<string, unknown>) => {
  return SUBSCRIPTION_KEYS.some((key) => record[key] === true);
};

const hasLegacySellkeySubscriptionAccess = (serviceInfo: Record<string, unknown>) => {
  const sellkeyInfo = SELLKEY_INFO_KEYS.map((key) => serviceInfo[key]).find(isRecord);

  // Legacy exception: allow active Sellkey subscribers through membership gates
  // until their remaining subscription period is handled in the new membership system.
  return sellkeyInfo ? hasSubscriptionFlag(sellkeyInfo) : false;
};

export const isPaidMembershipRequiredPath = (pathWithSearch: string) => {
  const pathname = pathWithSearch.split('?')[0] ?? pathWithSearch;

  return PAID_MEMBERSHIP_PATH_PREFIXES.some((paidPath) => {
    if (pathname === paidPath) return true;

    return pathname.startsWith(`${paidPath}/`);
  });
};

export const getMembershipConversionPath = (pathname: string) => {
  return pathname.startsWith(`${ROUTES.mobileRoot}/`)
    ? ROUTES.mobileMembership
    : ROUTES.moreMembership;
};
